import type { BudgetNoteRowDraft, BudgetNoteUsageEntryDraft } from '../types/budgetNote';
import type { BudgetMetaColKey } from './budgetNoteMetaSelection';
import { BUDGET_META_COL_KEYS } from './budgetNoteMetaSelection';

export type BudgetNoteSearchMatch =
  | {
      matchId: string;
      kind: 'meta';
      rowId: string;
      colKey: BudgetMetaColKey;
      start: number;
      end: number;
    }
  | {
      matchId: string;
      kind: 'usage_memo';
      rowId: string;
      entryId: string;
      start: number;
      end: number;
    };

export function normalizeBudgetSearchQuery(query: string): string {
  return query.trim();
}

export function textIncludesBudgetSearchQuery(text: string, query: string): boolean {
  const q = normalizeBudgetSearchQuery(query);
  if (!q) return false;
  return text.toLowerCase().includes(q.toLowerCase());
}

export function findBudgetTextOccurrences(
  text: string,
  query: string
): { start: number; end: number }[] {
  const q = normalizeBudgetSearchQuery(query);
  if (!q) return [];
  const lower = text.toLowerCase();
  const qLower = q.toLowerCase();
  const hits: { start: number; end: number }[] = [];
  let i = 0;
  while (i < lower.length) {
    const idx = lower.indexOf(qLower, i);
    if (idx === -1) break;
    hits.push({ start: idx, end: idx + q.length });
    i = idx + Math.max(1, q.length);
  }
  return hits;
}

export function buildBudgetMetaSearchMatchId(
  rowId: string,
  colKey: BudgetMetaColKey,
  start: number
): string {
  return `meta:${rowId}:${colKey}:${start}`;
}

export function buildBudgetUsageSearchMatchId(entryId: string, start: number): string {
  return `usage:${entryId}:${start}`;
}

export type BudgetNoteSearchHighlightSegment =
  | { type: 'text'; value: string }
  | { type: 'hit'; value: string; matchId: string; active: boolean };

export function buildBudgetSearchHighlightSegments(
  text: string,
  query: string,
  activeMatchId: string | null,
  buildMatchId: (start: number) => string
): BudgetNoteSearchHighlightSegment[] {
  const q = normalizeBudgetSearchQuery(query);
  if (!q) return [{ type: 'text', value: text }];
  const hits = findBudgetTextOccurrences(text, q);
  if (!hits.length) return [{ type: 'text', value: text }];
  const out: BudgetNoteSearchHighlightSegment[] = [];
  let cursor = 0;
  for (const hit of hits) {
    if (hit.start > cursor) {
      out.push({ type: 'text', value: text.slice(cursor, hit.start) });
    }
    const matchId = buildMatchId(hit.start);
    out.push({
      type: 'hit',
      value: text.slice(hit.start, hit.end),
      matchId,
      active: matchId === activeMatchId,
    });
    cursor = hit.end;
  }
  if (cursor < text.length) {
    out.push({ type: 'text', value: text.slice(cursor) });
  }
  return out;
}

export function collectBudgetNoteSearchMatches(params: {
  query: string;
  rows: BudgetNoteRowDraft[];
  entries: BudgetNoteUsageEntryDraft[];
  metaColsVisible: Record<BudgetMetaColKey, boolean>;
}): BudgetNoteSearchMatch[] {
  const q = normalizeBudgetSearchQuery(params.query);
  if (!q) return [];
  const out: BudgetNoteSearchMatch[] = [];

  for (const row of params.rows) {
    for (const colKey of BUDGET_META_COL_KEYS) {
      if (!params.metaColsVisible[colKey]) continue;
      const text = (row[colKey] ?? '') as string;
      for (const hit of findBudgetTextOccurrences(text, q)) {
        out.push({
          matchId: buildBudgetMetaSearchMatchId(row.id, colKey, hit.start),
          kind: 'meta',
          rowId: row.id,
          colKey,
          start: hit.start,
          end: hit.end,
        });
      }
    }
  }

  const rowOrder = new Map(params.rows.map((r, i) => [r.id, i]));
  const sortedEntries = [...params.entries].sort((a, b) => {
    const ra = rowOrder.get(a.row_id) ?? 0;
    const rb = rowOrder.get(b.row_id) ?? 0;
    if (ra !== rb) return ra - rb;
    return a.sort_order - b.sort_order || a.id.localeCompare(b.id);
  });

  for (const entry of sortedEntries) {
    const text = entry.memo ?? '';
    for (const hit of findBudgetTextOccurrences(text, q)) {
      out.push({
        matchId: buildBudgetUsageSearchMatchId(entry.id, hit.start),
        kind: 'usage_memo',
        rowId: entry.row_id,
        entryId: entry.id,
        start: hit.start,
        end: hit.end,
      });
    }
  }

  return out;
}
